import { useState, useCallback, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getFileUrl } from "@/lib/api";
import { X, GripVertical, Check, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Sortable photo item component
function SortablePhoto({ id, photo, index, onDelete, isReordering }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group aspect-[4/3] rounded-lg overflow-hidden bg-secondary",
        isDragging && "ring-2 ring-primary shadow-xl opacity-90",
        isReordering && "cursor-move"
      )}
    >
      {/* Photo number badge */}
      <div className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center">
        <span className="text-white text-xs font-mono">{index + 1}</span>
      </div>

      {/* Drag handle */}
      {isReordering && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-10 p-2 bg-black/70 rounded-full cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4 text-white" />
        </div>
      )}

      {/* Image */}
      <img
        src={getFileUrl(photo)}
        alt={`Photo ${index + 1}`}
        className="w-full h-full object-cover"
        draggable={false}
      />

      {/* Delete button (only visible when not reordering) */}
      {!isReordering && (
        <button
          onClick={() => onDelete(photo)}
          className="absolute top-2 right-2 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      )}

      {/* Drag overlay hint */}
      {isReordering && !isDragging && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium transition-opacity">
            Glisser pour réorganiser
          </span>
        </div>
      )}
    </div>
  );
}

export function SortablePhotoGallery({ 
  photos, 
  onDelete, 
  onReorder,
  scooterId,
  className 
}) {
  const [isReordering, setIsReordering] = useState(false);
  const [localPhotos, setLocalPhotos] = useState(photos);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update local photos when prop changes
  useEffect(() => {
    setLocalPhotos(photos);
    setHasChanges(false);
  }, [photos]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalPhotos((items) => {
        const oldIndex = items.findIndex((item) => item === active.id);
        const newIndex = items.findIndex((item) => item === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSaveOrder = async () => {
    setIsSaving(true);
    try {
      await onReorder(localPhotos);
      setHasChanges(false);
      setIsReordering(false);
    } catch (error) {
      console.error("Failed to save order:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalPhotos(photos);
    setHasChanges(false);
    setIsReordering(false);
  };

  const handleStartReorder = () => {
    setLocalPhotos(photos);
    setIsReordering(true);
  };

  if (!photos || photos.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {/* Reorder controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">
          {localPhotos.length} photo{localPhotos.length > 1 ? "s" : ""}
        </div>
        <div className="flex gap-2">
          {!isReordering ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartReorder}
              disabled={photos.length < 2}
              data-testid="btn-reorder-photos"
            >
              <GripVertical className="w-4 h-4 mr-2" />
              Réorganiser
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSaveOrder}
                disabled={!hasChanges || isSaving}
                className="bg-primary text-primary-foreground"
                data-testid="btn-save-photo-order"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Reordering hint */}
      {isReordering && (
        <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
          <p className="text-sm text-primary text-center">
            🔄 Mode réorganisation actif – Glissez et déposez les photos pour changer leur ordre
          </p>
        </div>
      )}

      {/* Photo grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={localPhotos} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {localPhotos.map((photo, idx) => (
              <SortablePhoto
                key={photo}
                id={photo}
                photo={photo}
                index={idx}
                onDelete={onDelete}
                isReordering={isReordering}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
